import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { TrashItem } from "../types";

const TRASH_FIELDS = `
  id
  type
  label
  name
  organizationId
  deletedAt
`;

export const trashApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTrash: builder.query<TrashItem[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Trash($organizationId: String) {
              trash(organizationId: $organizationId) {
                ${TRASH_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<TrashItem[]>(res, "trash"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((t) => ({
                type: "Trash" as const,
                id: `${t.type}:${t.id}`,
              })),
              { type: "Trash" as const, id: "LIST" },
            ]
          : [{ type: "Trash" as const, id: "LIST" }],
    }),

    restoreTrashItem: builder.mutation<boolean, { type: string; id: string }>({
      query: ({ type, id }) => ({
        body: {
          query: `
            mutation RestoreTrashItem($type: String!, $id: String!) {
              restoreTrashItem(type: $type, id: $id)
            }
          `,
          variables: { type, id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "restoreTrashItem"),

      invalidatesTags: [{ type: "Trash", id: "LIST" }],
    }),

    purgeTrashItem: builder.mutation<boolean, { type: string; id: string }>({
      query: ({ type, id }) => ({
        body: {
          query: `
            mutation PurgeTrashItem($type: String!, $id: String!) {
              purgeTrashItem(type: $type, id: $id)
            }
          `,
          variables: { type, id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "purgeTrashItem"),

      invalidatesTags: [{ type: "Trash", id: "LIST" }],
    }),

    emptyTrash: builder.mutation<number, string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            mutation EmptyTrash($organizationId: String) {
              emptyTrash(organizationId: $organizationId)
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<number>(res, "emptyTrash"),

      invalidatesTags: [{ type: "Trash", id: "LIST" }],
    }),
  }),
});

export const {
  useGetTrashQuery,
  useLazyGetTrashQuery,
  useRestoreTrashItemMutation,
  usePurgeTrashItemMutation,
  useEmptyTrashMutation,
} = trashApi;
